import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { getUnixTime } from 'date-fns';
import { MeteorObservable } from 'meteor-rxjs';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { Users } from 'server/shared/collections/users';
import {
  canBookInviteeForJob,
  userIncludesRequiredFieldsForJob,
} from 'server/shared/helpers/canBookInviteeForJob';
import { inviteeFitsJobCriteria } from 'server/shared/helpers/inviteeFitsJobCriteria';
import { removeTimeZoneOffset } from 'server/shared/helpers/removeTimeZoneOffset';
import { formatTimeNumber } from 'server/shared/helpers/time';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import { DeclineReasonEnum } from 'server/shared/models/declineReason.enum';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { Transfer } from 'server/shared/models/transfer';
import { DispoUser, PoolerUser } from 'server/shared/models/user';
import { getBookingConfirmationThroughDialog } from '../../../helpers/getBookingConfirmationThroughDialog';
import { getInviteesOfJob } from '../../../helpers/getInviteesOfJob';
import { inviteesHaveCancelCost } from '../../../helpers/inviteesHaveCancelCost';
import { processBookingWarnings } from '../../../helpers/job/processBookingWarnings';
import { CancelDialog } from '../../jobs/job-edit/dialogs/cancel-dialog/cancel-dialog.component';
import { DeclineInviteeDialog } from '../../jobs/job-edit/dialogs/decline-invitee-dialog/decline-invitee-dialog.component';
import { ManualCancelTimeDialogComponent } from '../../jobs/job-edit/dialogs/manual-cancel-time-dialog/manual-cancel-time-dialog.component';
import { TransferInviteeDialog } from '../../jobs/job-edit/dialogs/transfer-invitee-dialog/transfer-invitee-dialog.component';
import { CustomerService } from '../../services/customer.service';
import { PoolerService } from '../../services/pooler/pooler.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import {
  ConfirmationDialog,
  DialogData,
} from '../confirmation-dialog/confirmation.component';
import {
  RequiredFieldsDialog,
  RequiredFieldsDialogUpdatedFields,
} from '../required-fields-dialog/required-fields.component';

@Component({
  selector: 'app-job-invitee-list',
  templateUrl: 'job-invitee-list.component.html',
  styleUrls: ['job-invitee-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobInviteeListComponent implements OnInit, OnDestroy {
  invitees$ = new BehaviorSubject<Invitee[] | undefined>(undefined);
  @Input() set invitees(value: Invitee[] | undefined) {
    this.invitees$.next(value);
  }
  get invitees() {
    return this.invitees$.value;
  }
  condensed$ = new BehaviorSubject<boolean>(false);
  @Input() set condensed(value: boolean) {
    this.condensed$.next(value);
  }
  get condensed() {
    return this.condensed$.value;
  }
  userCounts$ = new BehaviorSubject<Map<string, number> | undefined>(undefined);
  @Input() set userCounts(value: Map<string, number> | undefined) {
    this.userCounts$.next(value);
  }
  get userCounts() {
    return this.userCounts$.value;
  }
  totalCount$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set totalCount(value: number | undefined) {
    this.totalCount$.next(value);
  }
  get totalCount() {
    return this.totalCount$.value;
  }

  displayHeader$ = new BehaviorSubject<boolean>(true);
  @Input() set displayHeader(value: boolean) {
    this.displayHeader$.next(value);
  }
  get displayHeader() {
    return this.displayHeader$.value;
  }

  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(value: Job | undefined) {
    this.job$.next(value);
    if (value) {
      this.initJob(value);
    }
  }
  get job() {
    return this.job$.value;
  }

  nameSearch$ = new BehaviorSubject<string>('');
  @Input() set nameSearch(value: string) {
    this.nameSearch$.next(value);
    this.getInvitees(this.loggedInCustomer);
  }
  get nameSearch() {
    return this.nameSearch$.value;
  }

  loggedInUser$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  @Input() set loggedInUser(value: DispoUser | undefined) {
    this.loggedInUser$.next(value);
  }
  get loggedInUser() {
    return this.loggedInUser$.value;
  }

  jobCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  @Input() set jobCustomer(value: Customer | undefined) {
    this.jobCustomer$.next(value);
  }
  get jobCustomer() {
    return this.jobCustomer$.value;
  }

  loggedInCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  get loggedInCustomer() {
    return this.loggedInCustomer$.value;
  }

  sharedPoolCustomers$ = new BehaviorSubject<Customer[]>([]);
  get sharedPoolCustomers() {
    return this.sharedPoolCustomers$.value;
  }

  transfer$ = new BehaviorSubject<Transfer | undefined>(undefined);
  get transfer() {
    return this.transfer$.value;
  }

  i18n = this.translationService.getTranslations('jobs');
  jobStatusEnum = JobStatusEnum;
  inviteeStatusEnum = InviteeStatusEnum;

  jobUsersSubscription?: Subscription;
  previousJobSubscription?: Subscription;
  private destroyed$ = new Subject<void>();

  acceptedInvitees$ = new BehaviorSubject<Invitee[]>([]);
  transferredInvitees$ = new BehaviorSubject<Invitee[]>([]);
  availableInvitees$ = new BehaviorSubject<Invitee[]>([]);
  partiallyAvailableInvites$ = new BehaviorSubject<Invitee[]>([]);
  openInvitees$ = new BehaviorSubject<Invitee[]>([]);
  workingInvitees$ = new BehaviorSubject<Invitee[]>([]);
  unavailableInvitees$ = new BehaviorSubject<Invitee[]>([]);
  declinedInvitees$ = new BehaviorSubject<Invitee[]>([]);
  canceledInvitees$ = new BehaviorSubject<Invitee[]>([]);

  constructor(
    private translationService: TranslationService,
    private customerService: CustomerService,
    private poolerService: PoolerService,
    private dialog: MatDialog,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    if (this.job) {
      this.initJob(this.job);
    } else {
      throw new Error('missing Job in invitees-list!');
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.jobUsersSubscription) {
      this.jobUsersSubscription.unsubscribe();
    }
    if (this.previousJobSubscription) {
      this.previousJobSubscription.unsubscribe();
    }
  }

  async subscribeData() {
    if (this.job) {
      this.jobUsersSubscription = MeteorObservable.subscribe(
        'usersOfJob',
        this.job?._id,
        true,
      ).subscribe();
    }
  }

  async initJob(job: Job) {
    const customer = await this.customerService.readyDispoCustomer;
    this.loggedInCustomer$.next(customer);
    this.poolerService.readyJobSharedPoolCustomers$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customers) => {
        this.sharedPoolCustomers$.next(customers);
        if (job) {
          this.getInvitees(this.loggedInCustomer);
        }
      });

    this.getInvitees(this.loggedInCustomer);
  }

  getInvitees(loggedInCustomer?: Customer) {
    if (!this.job || !this.invitees?.length || !this.loggedInUser) {
      return;
    }
    if (this.job.customer.id != this.loggedInCustomer?._id) {
      let transferIndex = this.job.transfers?.findIndex(
        (transfer) => transfer.targetCustomer.id == this.loggedInCustomer?._id,
      );
      if (transferIndex !== undefined && transferIndex > -1) {
        this.transfer$.next(this.job.transfers?.[transferIndex]);
      }
    }
    if (!this.loggedInUser?.customerId) return;
    const invitees = getInviteesOfJob(
      this.job,
      this.invitees,
      this.loggedInUser.customerId,
      this.sharedPoolCustomers,
      this.nameSearch,
    );
    if (loggedInCustomer?.prioritizeEmployeeLastName) {
      invitees.sort((a, b) => {
        return a.personalData.lastName.localeCompare(b.personalData.lastName);
      });
    } else {
      invitees.sort((a, b) => {
        return a.personalData.firstName.localeCompare(b.personalData.firstName);
      });
    }

    const customers = [
      this.loggedInCustomer?._id ?? '',
      ...(this.loggedInCustomer?.sharedPoolPriorities || []).map(
        (p) => p.customerId,
      ),
    ];
    const priorityByCustomer = new Map<string, number>(
      customers.map((customerId, index) => [customerId, index + 1]),
    );
    invitees.sort(
      (a, b) =>
        (priorityByCustomer.get(b.customer?.id || '') || 0) -
        (priorityByCustomer.get(a.customer?.id || '') || 0),
    );
    this.acceptedInvitees$.next(
      invitees.filter((invitee) => invitee.status == InviteeStatusEnum.BOOKED),
    );
    this.transferredInvitees$.next(
      invitees.filter(
        (invitee) => invitee.status == InviteeStatusEnum.TRANSFERRED,
      ),
    );
    this.availableInvitees$.next(
      invitees.filter((invitee) =>
        [
          InviteeStatusEnum.AVAILABLE,
          InviteeStatusEnum.POSSIBLE_CANDIDATE,
        ].includes(invitee.status),
      ),
    );
    this.partiallyAvailableInvites$.next(
      invitees.filter(
        (invitee) => invitee.status == InviteeStatusEnum.PARTIAL_AVAILABLE,
      ),
    );
    this.openInvitees$.next(
      invitees.filter((invitee) => invitee.status == InviteeStatusEnum.OPEN),
    );
    this.workingInvitees$.next(
      invitees.filter((invitee) => invitee.status == InviteeStatusEnum.WORKING),
    );
    this.unavailableInvitees$.next(
      invitees.filter(
        (invitee) => invitee.status == InviteeStatusEnum.UNAVAILABLE,
      ),
    );
    this.declinedInvitees$.next(
      invitees.filter(
        (invitee) => invitee.status == InviteeStatusEnum.DECLINED,
      ),
    );
    this.canceledInvitees$.next(
      invitees.filter(
        (invitee) => invitee.status == InviteeStatusEnum.CANCELED,
      ),
    );
  }

  trackInvitee(index: number, item: Invitee) {
    return item.id;
  }

  async bookUser(invitee: Invitee) {
    const inviteeUser = Users.findOne(invitee.userId) as PoolerUser;
    if (!this.invitees || !this.jobCustomer) {
      return;
    }

    if (
      inviteeUser &&
      !userIncludesRequiredFieldsForJob(inviteeUser, this.jobCustomer)
    ) {
      const dialogRef = this.dialog.open(RequiredFieldsDialog, {
        data: {
          user: inviteeUser,
          requiredFields: this.jobCustomer?.requiredJobFields,
        },
      });

      dialogRef
        .afterClosed()
        .subscribe((result: RequiredFieldsDialogUpdatedFields) => {
          if (result) {
            Meteor.call(
              'updateRequiredFields',
              inviteeUser._id,
              result,
              (error: Meteor.Error) => {
                if (error) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  return;
                }
                this.bookUser(invitee);
              },
            );
          }
        });
      return;
    }
    if (!this.job) {
      this.toastService.add({ type: 'ERROR', message: 'missingJob' });
      return;
    }
    const canBookResult = canBookInviteeForJob(
      inviteeUser,
      this.job,
      this.loggedInCustomer,
    );
    if (
      !(inviteeUser && canBookResult.canBook) &&
      !invitee.customer.isExternal
    ) {
      if (canBookResult.reason) {
        this.toastService.add({
          type: 'ERROR',
          message: canBookResult.reason,
        });
      }
      return;
    }

    if (
      this.job &&
      inviteeUser &&
      !inviteeFitsJobCriteria(inviteeUser, this.job)
    ) {
      this.toastService.add({
        type: 'ERROR',
        message: 'inviteeNoLongerFitCriteria',
      });
      return;
    }

    let inviteeIndex = this.invitees?.findIndex(
      (invitee1) => invitee1.id == invitee.id,
    );
    if (
      inviteeIndex === -1 ||
      inviteeIndex === undefined ||
      !this.job?.invitees?.[inviteeIndex]
    )
      return;

    const notes: string[] = [];
    if (this.invitees[inviteeIndex].additionalInfo) {
      notes.push(this.invitees[inviteeIndex].additionalInfo || '');
    }
    this.invitees[inviteeIndex]?.partialTimes?.forEach((partialTime) => {
      const note =
        this.i18n?.onlyAvailableFrom +
        formatTimeNumber(partialTime.from) +
        this.i18n?.onlyAvailableTo +
        formatTimeNumber(partialTime.to) +
        this.i18n?.onlyAvailableEnd;
      notes.push(note);
    });
    let text = this.i18n?.confirmPoolerWithNotes as string;
    if (notes.length) {
      notes.forEach((note) => {
        text =
          text +
          `
    ` +
          note;
      });
    }
    if (
      this.job.customer.id !== this.loggedInCustomer?._id &&
      this.transfer?.preventDirectAccept
    ) {
      if (notes.length) {
        this.openBookingConfirmDialog(invitee, text);
        return;
      } else {
        this.invitees[inviteeIndex].status =
          InviteeStatusEnum.POSSIBLE_CANDIDATE;
      }
    } else {
      if (notes.length) {
        this.openBookingConfirmDialog(invitee, text);
        return;
      }
    }

    const poolerType = this.loggedInCustomer?.poolerTypes?.find(
      (poolerType) =>
        poolerType.id ===
          inviteeUser?.customerInformation?.[invitee.customer.id]?.poolerType
            ?.id || poolerType.id === invitee.personalData.poolerType?.id,
    );

    const allConfirmed = await processBookingWarnings(
      [
        {
          user: inviteeUser,
          poolerType: poolerType,
        },
      ],
      this.dialog,
      this.i18n,
    );

    if (!allConfirmed) {
      return;
    }
    this.apiBookInvitee(invitee, allConfirmed);
  }

  apiBookInvitee(invitee: Invitee, warningConfirmed?: boolean) {
    if (!this.job) return;
    Meteor.call(
      'bookInvitee',
      {
        jobId: this.job._id,
        inviteeId: invitee.id,
        warningConfirmed,
      },
      (
        error: Meteor.Error,
        result?: {
          success: boolean;
          isPossibleCandidate: boolean;
          error?: Meteor.Error;
        },
      ) => {
        if (!this.invitees) {
          throw new Error('suddenly missing invitees in job-invitee-list');
        }
        if (result?.error) {
          error = result.error;
        }
        if (error) {
          if (error.error === 'inviteeNoLongerFitCriteria') {
            this.toastService.add({
              type: 'ERROR',
              message: 'inviteeNoLongerFitCriteria',
            });
            return;
          }
          if (error.error === 'CarePeoplePoolerBookingFailed') {
            this.toastService.add({
              type: 'ERROR',
              message: 'carePeoplePoolerBookingFailed',
            });
            return;
          }
          if (error.error === 'VokusPoolerBookingFailed') {
            this.toastService.add({
              type: 'ERROR',
              message: 'vokusPoolerBookingFailed',
            });
            return;
          }
          if (error.error === 'bookingWarningRequired') {
            const inviteeUser = Users.findOne(invitee.userId) as PoolerUser;
            let poolerType = this.loggedInCustomer?.poolerTypes?.find(
              (poolerType) =>
                poolerType.id === invitee.personalData.poolerType?.id ||
                poolerType.id ===
                  inviteeUser?.customerInformation?.[invitee.customer.id]
                    ?.poolerType?.id,
            );
            let text =
              poolerType?.bookingWarningMessage ||
              this.i18n.poolerTypeBookingWarningDefaultMessage?.replace(
                '{{poolerType}}',
                poolerType?.displayName || '',
              );
            this.openBookingConfirmDialog(invitee, text, true);
            return;
          }
          this.toastService.add({
            type: 'ERROR',
            message: 'serverError',
          });
          return;
        }
        const message = result?.isPossibleCandidate
          ? 'userSubmittedAsPossibleCandidate'
          : 'userBookedSuccessfullyJob';
        this.toastService.add({
          type: 'SUCCESS',
          message,
        });
      },
    );
  }

  openBookingConfirmDialog(
    invitee: Invitee,
    text: string,
    isWarningConfirmation?: boolean,
  ) {
    if (!this.job) return;
    getBookingConfirmationThroughDialog(
      this.dialog,
      invitee.id + '-book-warning',
      text,
    ).then((result: string | undefined) => {
      if (!this.invitees) {
        throw new Error('suddenly missing invitees in job-invitee-list');
      }
      if (result && this.job) {
        this.apiBookInvitee(invitee, isWarningConfirmation);
      }
    });
  }

  overrideInviteeStatus(status: InviteeStatusEnum, inviteeId: string) {
    const setStatus = () => {
      if (!this.job) {
        throw new Error('missing job in job-invitee-list');
      }
      if (!this.invitees) {
        throw new Error('suddenly missing invitees in job-invitee-list');
      }
      let inviteeIndex = this.invitees.findIndex(
        (invitee) => invitee.id == inviteeId,
      );
      if (inviteeIndex === -1 || inviteeIndex === undefined) return;
      let oldStatus = this.invitees[inviteeIndex].status;
      let oldUpdatedAt = this.invitees[inviteeIndex].updatedAt;
      this.invitees[inviteeIndex].status = status;
      this.invitees[inviteeIndex].updatedAt = getUnixTime(
        removeTimeZoneOffset(new Date()),
      );
      Meteor.call(
        'setInviteeStatus',
        this.job._id,
        inviteeId,
        status,
        (error: Meteor.Error) => {
          if (error) {
            if (
              inviteeIndex === -1 ||
              inviteeIndex === undefined ||
              !this.job?.invitees?.[inviteeIndex]
            )
              return;
            if (!this.invitees) {
              throw new Error('suddenly missing invitees in job-invitee-list');
            }
            this.invitees[inviteeIndex].status = oldStatus;
            this.invitees[inviteeIndex].updatedAt = oldUpdatedAt;
            this.toastService.add({ type: 'ERROR', message: 'serverError' });
            return;
          }
        },
      );
    };
    if (status === InviteeStatusEnum.UNAVAILABLE) {
      setStatus();
    } else {
      const data: DialogData = {
        id: 'overrideInviteeStatus',
        title: (this.i18n?.important as string) || 'Wichtig',
        text:
          (this.i18n?.confirmPoolerAvailability as string) ||
          'Sie erfassen eine manuelle Dienstzusage. Bitte bestätigen Sie, dass Sie vorgängig eine mündliche Zusage des Mitarbeitenden für diesen Dienst erhalten haben.',
      };
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        data,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          setStatus();
        }
      });
    }
  }

  cancelAcceptedInvitee(
    userId: string,
    inviteeId: string,
    manualCancelTime?: Date,
  ) {
    const invitee = this.job?.invitees.find(
      (invitee) => invitee.id === inviteeId,
    );
    if (!invitee || !this.job) return;

    // Check if manual cancel timestamp is required
    const isExternalInvitee =
      invitee.customer.id !== this.loggedInUser?.customerId;
    const shouldUseManualCancelTime =
      this.loggedInCustomer?.enableManualCancelTimestampForExternal &&
      isExternalInvitee;

    if (shouldUseManualCancelTime && !manualCancelTime) {
      const dialogRef = this.dialog.open(ManualCancelTimeDialogComponent);
      dialogRef.afterClosed().subscribe((timestamp) => {
        if (timestamp) {
          this.cancelAcceptedInvitee(userId, inviteeId, timestamp);
        }
      });
      return;
    }

    if (inviteesHaveCancelCost(this.job)) {
      const confirmDialog = this.dialog.open(ConfirmationDialog, {
        data: {
          id: 'cancelAcceptedInvitee',
          title: this.i18n.cancelCostWarningTitle || 'Mögliche Stornokosten',
          text:
            this.i18n.cancelCostWarningMessage ||
            'Sie stornieren einen Mitarbeitenden innerhalb von weniger Stunden vor Dienstbeginn. Bei gewissen Vermittlern entstehen dadurch möglicherweise Stornokosten.',
        },
      });

      confirmDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.openCancelInviteeDialog(inviteeId, manualCancelTime);
        }
      });
    } else {
      this.openCancelInviteeDialog(inviteeId, manualCancelTime);
    }
  }

  openCancelInviteeDialog(inviteeId: string, manualCancelTime?: Date) {
    let cancelReasons =
      this.loggedInCustomer?.cancelReasons || defaultCancelReasons;

    const dialogRef = this.dialog.open(CancelDialog, {
      data: { reasons: cancelReasons },
    });
    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          reason: CancelReason;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (result?.reason) {
            if (!this.job) return;
            Meteor.call(
              'cancelInvitee',
              this.job._id,
              inviteeId,
              result.reason,
              result.reasonDescription,
              result.informInviteeOfReason,
              manualCancelTime,
              (error: Meteor.Error) => {
                if (error) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  return;
                }
                //@ts-ignore
                window.dataLayer.push({
                  event: 'cancelInvitee',
                  inviteeCancelReason: result.reason.displayName,
                });
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'inviteeCanceled',
                });
              },
            );
          }
        },
      );
  }

  openDeclineInviteeDialog(inviteeId: string) {
    const dialogRef = this.dialog.open(DeclineInviteeDialog);
    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          reason: DeclineReasonEnum;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (result?.reason) {
            if (!this.job) return;
            Meteor.call(
              'declineInvitee',
              this.job._id,
              inviteeId,
              result.reason,
              result.reasonDescription,
              result.informInviteeOfReason,
              (error: Meteor.Error) => {
                if (error) {
                  console.log('error', error);
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  return;
                }
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'inviteeCanceled',
                });
              },
            );
          }
        },
      );
  }

  reAskInvitee(invitee: Invitee) {
    if (!this.job) return;
    Meteor.call(
      'reAskInvitee',
      this.job._id,
      invitee,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.toastService.add({ type: 'SUCCESS', message: 'reAskedInvitee' });
      },
    );
  }

  transferAcceptedInvitee(inviteeUserId: string) {
    this.dialog.open(TransferInviteeDialog, {
      data: {
        job: this.job,
        inviteeUserId: inviteeUserId,
      },
    });
  }

  hasEnteredScreen$ = new BehaviorSubject<boolean>(false);
  enteredScreen() {
    if (this.hasEnteredScreen$.value) return;
    this.subscribeData();
    this.hasEnteredScreen$.next(true);
  }
}
